// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
// Every Flameco employee app, in the order the app switcher shows them, with
// the icon-plate hue World's home gives each one, and each app's main pages
// for the shared "Search everything" box. One list for all apps: edit it here
// and re-sync, never in an app's copy.

export type FlamecoAppKey =
  | 'world'
  | 'mfg'
  | 'internal'
  | 'alpha'
  | 'analytics'
  | 'legacy'
  | 'perf'
  | 'lms'
  | 'portal';

export interface FlamecoApp {
  key: FlamecoAppKey;
  /** Shown after the FLAMECO lockup in the top bar (mono caps) and in the switcher. */
  name: string;
  /** Shown in the app switcher. */
  short: string;
  /** One line for launcher tiles and search results. */
  description: string;
  url: string;
  /** OKLCH hue of the app's icon plate (World's home tiles); null = neutral plate. */
  hue: number | null;
  /** Only reachable on the Flameco network. */
  onNetwork?: boolean;
}

export const FLAMECO_APPS: FlamecoApp[] = [
  { key: 'world', name: 'World', short: 'World', hue: 45, description: 'Company news, people, calendar and tribal knowledge', url: 'https://world.flameco.com/' },
  { key: 'mfg', name: 'Manufacturing', short: 'Manufacturing', hue: 260, description: 'Work orders, shipping, receiving and quality on the floor', url: 'https://mfg.flameco.com/' },
  { key: 'internal', name: 'Internal Tools', short: 'Internal Tools', hue: 240, description: 'Engineering calculators, quoting and planning tools', url: 'https://internal.flameco.com/' },
  { key: 'alpha', name: 'Alpha', short: 'Alpha', hue: 285, description: 'Ask Flameco AI, run workflows and track KPIs', url: 'https://alpha.flameco.com/' },
  { key: 'analytics', name: 'Analytics', short: 'Analytics', hue: 230, description: 'Reports across finance, sales, production and supply chain', url: 'https://analytics.flameco.com/' },
  { key: 'legacy', name: 'Legacy ERP', short: 'Legacy ERP', hue: null, description: 'Historical orders and requests from the old ERP', url: 'http://192.168.1.101:3002/', onNetwork: true },
  { key: 'perf', name: 'Performance Reviews', short: 'Perf Reviews', hue: 10, description: 'Reviews, goals and check-ins', url: 'https://performancereview.flameco.com/' },
  { key: 'lms', name: 'Learning', short: 'Learning', hue: 215, description: 'Training courses and certifications', url: 'https://agreeable-mushroom-0904ffa10.7.azurestaticapps.net/login/' },
  { key: 'portal', name: 'Customer Portal', short: 'Customer Portal', hue: 330, description: 'What customers see: orders, quotes and the parts store', url: 'https://app.flameco.com/login?redirect=%2Fcustomer%2Fdashboard' },
];

/** A page another app can link straight to (search results). Paths are on that app's own address. */
export interface FlamecoPage {
  app: FlamecoAppKey;
  name: string;
  path: string;
  keywords?: string;
}

/** The main pages of each Flameco app, for "Search everything". */
export const FLAMECO_PAGES: FlamecoPage[] = [
  { app: 'world', name: 'Company Calendar', path: '/calendar', keywords: 'events holidays birthdays meetings' },
  { app: 'world', name: 'Company News Feed', path: '/newsfeed', keywords: 'news announcements' },
  { app: 'world', name: 'Tribal Knowledge', path: '/knowledge', keywords: 'know-how questions answers' },
  { app: 'world', name: 'Organization & People', path: '/team', keywords: 'org chart team people directory' },
  { app: 'world', name: 'Merchandise Hierarchy', path: '/merchandise-hierarchy', keywords: 'products catalogue plant' },
  { app: 'world', name: 'Kudos', path: '/kudos', keywords: 'recognition thanks' },
  { app: 'world', name: 'My Posts', path: '/my-posts' },
  { app: 'mfg', name: 'Work order operations', path: '/work-order-operations', keywords: 'wo work order assembly build start pause end scan paperwork' },
  { app: 'mfg', name: 'Shift manager', path: '/work-order-operations?tab=shift', keywords: 'bulk work orders shift wo' },
  { app: 'mfg', name: 'Sales order work orders', path: '/work-order-operations?tab=so', keywords: 'so sales order lookup search wo' },
  { app: 'mfg', name: 'Laser operations', path: '/laser-operations', keywords: 'laser queue quality dxf nest' },
  { app: 'mfg', name: 'Main production queue', path: '/production-queue-user', keywords: 'queue production' },
  { app: 'mfg', name: 'Production TV', path: '/production-tv', keywords: 'tv display board scoreboard buffer wall' },
  { app: 'mfg', name: 'Queue TV display', path: '/production-queue-tv?display=tv', keywords: 'tv display wall queue' },
  { app: 'mfg', name: 'Shared calendar', path: '/shared-calendar', keywords: 'time off pto vacation schedule' },
  { app: 'mfg', name: 'Flame Cell operations', path: '/flame-cell-build-queue', keywords: 'flame cell rolling build queue' },
  { app: 'mfg', name: 'BOM pipeline', path: '/bom-automation-queue', keywords: 'bom automation queue cpq' },
  { app: 'mfg', name: 'Production planner', path: '/production-planning', keywords: 'planning schedule' },
  { app: 'mfg', name: 'Shipping operations', path: '/shipping', keywords: 'ship item fulfillment packing' },
  { app: 'mfg', name: 'Customer pickup', path: '/customer-pickup', keywords: 'pickup' },
  { app: 'mfg', name: 'Packing assistant', path: '/packing', keywords: 'pack box crate' },
  { app: 'mfg', name: 'Print packing tag', path: '/packing-tag', keywords: 'packing tag label print' },
  { app: 'mfg', name: 'Item fulfillments', path: '/fulfillment', keywords: 'if fulfillment shipped ship' },
  { app: 'mfg', name: 'Receiving operations', path: '/receiving', keywords: 'receive purchase order po' },
  { app: 'mfg', name: 'Receive RMA', path: '/rma', keywords: 'return rma' },
  { app: 'mfg', name: 'Vendor packing slip', path: '/vendor-packing-slip', keywords: 'vendor packing slip receiving po' },
  { app: 'mfg', name: 'MTR work items', path: '/mtr-work-items', keywords: 'mtr material test report' },
  { app: 'mfg', name: 'MTR search', path: '/mtr-search', keywords: 'mtr heat lot certificate material test report' },
  { app: 'mfg', name: 'MTR scanning', path: '/mtr-scanning', keywords: 'mtr scan upload' },
  { app: 'mfg', name: 'Metrics', path: '/analytics', keywords: 'analytics kpi' },
  { app: 'mfg', name: 'On-time delivery', path: '/analytics/on-time-delivery', keywords: 'otd late shipped metrics' },
  { app: 'mfg', name: 'Adjusted on-time delivery', path: '/analytics/adjusted-on-time-delivery', keywords: 'otd adjusted metrics' },
  { app: 'mfg', name: 'Production scoreboard', path: '/analytics/production-scoreboard', keywords: 'scoreboard output metrics' },
  { app: 'mfg', name: 'What goes wrong most', path: '/analytics/rejection-issues', keywords: 'rejection issues themes quality metrics' },
  { app: 'mfg', name: 'Rejections by person', path: '/analytics/rejections-by-person', keywords: 'rejections people quality metrics' },
  { app: 'mfg', name: 'Internal piping', path: '/internal-piping', keywords: 'piping calculator drawing' },
  { app: 'mfg', name: 'Quality management', path: '/qms', keywords: 'qms rcca rejections' },
  { app: 'mfg', name: 'Repair control room', path: '/repair', keywords: 'repair rma rework' },
  { app: 'mfg', name: 'Learning center', path: '/learning', keywords: 'training guides how to' },
  { app: 'mfg', name: 'Release reports', path: '/feature-releases', keywords: 'whats new release notes changes' },
  { app: 'mfg', name: 'Submit feedback', path: '/feedback', keywords: 'feedback bug idea report problem' },
  { app: 'mfg', name: 'Customer portal cases', path: '/customer-portal-cases', keywords: 'support cases customer portal tickets' },
  { app: 'mfg', name: 'Role management', path: '/role-management', keywords: 'roles users access' },
  { app: 'internal', name: 'MTO configurator', path: '/mto-configurator', keywords: 'make to order costing labor bom' },
  { app: 'internal', name: 'Flame cell calculator', path: '/flame-cell-calculator', keywords: 'flame cell flow pressure' },
  { app: 'internal', name: 'Production planning', path: '/production-planning', keywords: 'weekly schedule welders capacity' },
  { app: 'internal', name: 'Flame arrestors', path: '/flame-arrestors', keywords: 'tia sizing threaded inline arrestor' },
  { app: 'internal', name: 'Laser quote', path: '/laser-quote', keywords: 'laser quote cost' },
  { app: 'internal', name: 'Tribal knowledge', path: '/tribal-knowledge', keywords: 'know-how' },
  { app: 'internal', name: 'Balanced strategic plan', path: '/balanced-strategic-plan', keywords: 'strategy scorecard' },
  { app: 'internal', name: 'Users and roles', path: '/user-management', keywords: 'users roles access' },
  { app: 'alpha', name: 'Chat with Flameco AI', path: '/chat', keywords: 'ask ai flameco chat' },
  { app: 'alpha', name: 'Workflows', path: '/workflows', keywords: 'bom audit document chain' },
  { app: 'alpha', name: 'KPIs', path: '/kpis', keywords: 'metrics' },
  { app: 'alpha', name: 'Connectors', path: '/connectors', keywords: 'netsuite oauth integrations' },
  { app: 'alpha', name: 'Documents', path: '/documents', keywords: 'upload files' },
  { app: 'alpha', name: 'Skills', path: '/skills' },
  { app: 'alpha', name: 'Past conversations', path: '/threads', keywords: 'doug chat history threads' },
  { app: 'alpha', name: 'Usage', path: '/usage', keywords: 'cost tokens usage' },
  { app: 'legacy', name: 'Orders', path: '/orders', keywords: 'old orders serial invoice burner' },
  { app: 'legacy', name: 'Requests', path: '/requests' },
  { app: 'legacy', name: 'New request', path: '/requests/new' },
];

/** World's people page (the account menu's "My profile in World"). */
export const worldProfileUrl = (_employeeId: string, worldUrl = 'https://world.flameco.com/') =>
  `${worldUrl.replace(/\/+$/, '')}/team`;

/** An app's address joined with a path on it. */
export const appUrl = (app: FlamecoApp, path = '/') => `${app.url.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`;

/**
 * The list with local addresses swapped in, for development:
 * `{ world: 'http://localhost:3005/' }`. Apps pass their env var
 * (NEXT_PUBLIC_FLAMECO_APP_URLS / REACT_APP_FLAMECO_APP_URLS, JSON) here.
 */
export function resolveApps(overrides?: Partial<Record<FlamecoAppKey, string>> | string | null): FlamecoApp[] {
  let o: Partial<Record<FlamecoAppKey, string>> = {};
  if (typeof overrides === 'string' && overrides.trim()) {
    try {
      o = JSON.parse(overrides);
    } catch {
      o = {};
    }
  } else if (overrides && typeof overrides === 'object') {
    o = overrides;
  }
  return FLAMECO_APPS.map((a) => (o[a.key] ? { ...a, url: o[a.key] as string, onNetwork: false } : a));
}

/**
 * Category colours (OKLCH hues) shared by every Flameco app (UX Standard v2.1):
 * the bold strip on cards, the section heading and the side-nav group label.
 * null = the neutral (graphite) category. The same kind of work gets the same
 * colour in every app.
 */
export const FLAMECO_CATEGORY_HUES = {
  floor: 45,
  planning: 250,
  quality: 150,
  analytics: 195,
  tools: 295,
  admin: null,
  organization: 175,
  apps: 45,
  employment: 295,
  knowledge: 175,
  engineering: 25,
} as const satisfies Record<string, number | null>;
